
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { API_TReport } from "@/models/report";
import { API_ROUTES } from "@/utils/API";
import { SMCases, SMUser } from "@/store";
import { API_TCase } from "@/models/cases";

import {
  SKILLED_WORKER_COS_SERVICE_TYPE,
  SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE,
  SKILLED_WORKER_SERVICE_TYPE,
  COS_SKILLED_WORKER_INSIDE_SERVICE,
  COS_SKILLED_WORKER_SERVICE,
  SKILLED_WORKER_INSIDE_SERVICE_TYPE,
  CANDIDATE_ASSESSMENT_SERVICE_TYPE,
  EC_COMPLETED_STATUSES,
} from "@/constants/services";

import {
  LOW_RISK_STATUS,
  HIGH_RISK_STATUS,
  CANDIDATE_WIDGET_TYPE,
  EMPLOYER_INSIDE_WIDGET_TYPE,
  EMPLOYER_OUTSIDE_WIDGET_TYPE,
  ASSESSMENT_REPORT_STATUS_KEYS,
} from "@/constants/assessment";

const CLASS = "widget";

const DEFAULT_REPORT = {
  content: "",
  eligibility_result: LOW_RISK_STATUS,
  user_name: "",
  widget_type: CANDIDATE_WIDGET_TYPE,
  has_child_case: false,
  is_sl_started: false,
  if_sponsor: false,
};

const ICONS = {
  checkmarkOutline: "checkmark-outline",
};

const STEP_NAME_GET_SPONSOR_LICENCE = "getSponsorLicence";

@Component({
  components: {
    WidgetSkilledWorkerVisa: () =>
      import("@/modules/Services/Components/Widgets/WidgetSkilledWorkerVisa.vue"),
    PopupSkilledWorkerVisaService: () =>
      import("@/modules/Compliance/Popups/PopupSkilledWorkerVisaService.vue"),
    NewWidgetSkilledWorkerVisa: () =>
      import("@/components/V2/WidgetSkilledWorkerVisa/NewWidgetSkilledWorkerVisa.vue"),
    SubmitRequestVisaPopUp: () => import("@/components/Popups/SubmitRequestVisaPopUp.vue"),
    // WidgetGetSponsorLicenceDialog: () =>
    //   import(
    //     "@/modules/Compliance/Widgets/WidgetGetSponsorLicence/WidgetGetSponsorLicenceDialog.vue"
    //   ),
    NewWidgetGetSponsorLicenceDialog: () =>
      import("@/components/V2/WidgetGetSponsorLicence/NewWidgetGetSponsorLicenceDialog.vue"),
    UnarchiveCandidate: () =>
      import("@/modules/Services/Components/ArchiveCandidate/UnarchiveCandidate.vue"),
    ArchiveCandidate: () =>
      import("@/modules/Services/Components/ArchiveCandidate/ArchiveCandidate.vue"),
  },
})
export default class WidgetReportGenerated extends Vue {
  @Prop({ type: Object, default: () => DEFAULT_REPORT }) readonly report!: API_TReport;
  @Prop({ type: Number }) readonly serviceId?: number;
  @Prop({ type: Boolean, default: false }) readonly reportLoading!: boolean;

  icons = ICONS;

  loading = false;

  get widgetType() {
    return this.report.widget_type;
  }

  get status() {
    return this.report.eligibility_result;
  }

  get slCaseId() {
    return SMUser.user?.sl_case_id ?? 0; // Is company has SL case;
  }

  async goToSLCase() {
    const { slCaseId } = this;

    if (!slCaseId) return;

    await this.$router.push(`/cases/${slCaseId}/dashboard/${SMCases.tabId}`);
  }

  // TODO: Refactoring, and make some shared implementation;
  get childServiceId(): number {
    const service = SMCases.case as API_TCase;

    return Number(service?.child_case_id ?? 0);
  }

  get isStartVisaApplicationButtonDisabled(): boolean {
    if (SMUser.isExpert) return !(SMCases.case as API_TCase)?.company_is_sponsor;

    return !SMUser.isSponsorConfirmation;
  }

  get startVisaApplicationButtonHint(): string {
    return this.isStartVisaApplicationButtonDisabled
      ? this.$tc("button.startVisaApplicationButtonHint")
      : "";
  }

  @Watch("childServiceId", { immediate: true })
  childServiceIdChanged(val: boolean) {
    if (!val) return;

    this.fetchChildService();
  }

  childService = {};

  async fetchChildService() {
    const id = this.childServiceId;

    if (!id) return;

    const { data } = await this.$http.get(API_ROUTES.CASE_ID(id));

    if (!data) return;

    this.childService = data;
  }

  get isCosPlusSwStarted() {
    if (this.reportLoading) return true;

    if (!this.childServiceId) return false;

    const service = this.childService as API_TCase;

    if (!service?.case_template?.case_type) return false;

    return (
      this.report.has_child_case &&
      [
        SKILLED_WORKER_SERVICE_TYPE,
        SKILLED_WORKER_INSIDE_SERVICE_TYPE,
        SKILLED_WORKER_COS_SERVICE_TYPE,
        SKILLED_WORKER_COS_INSIDE_SERVICE_TYPE,
      ].includes(service.case_template?.case_type)
    );
  }

  get isSlStarted() {
    return this.report.is_sl_started;
  }

  get hasStatus() {
    return this.status && ASSESSMENT_REPORT_STATUS_KEYS.includes(this.status);
  }

  get classes() {
    const { status, isCosPlusSwStarted } = this;

    if (isCosPlusSwStarted)
      return {
        [`${CLASS}_cos-plus-sw-is-started`]: true,
      };

    return {
      [`${CLASS}__${status}`]: status,
    };
  }

  get isEligible() {
    return !(this.status === HIGH_RISK_STATUS);
  }

  get isSponsor() {
    return this.report.if_sponsor;
  }

  get isCandidate() {
    return this.widgetType === CANDIDATE_WIDGET_TYPE;
  }

  get isInside() {
    return this.widgetType == EMPLOYER_INSIDE_WIDGET_TYPE;
  }

  get isOutside() {
    return this.widgetType == EMPLOYER_OUTSIDE_WIDGET_TYPE;
  }

  // Start SW
  get showStartVisaApplicationButton() {
    return this.report.show_start_sw_button;
  }

  // Start SL
  get showRequestSponsorLicenceButton() {
    return this.report.show_star_sl_button;
  }

  // Continue SL
  get showContinueSponsorLicenseCaseButton() {
    return this.report.show_continue_sl_button;
  }

  get description() {
    return this.$tc(`statuses.${this.status}.description.${this.widgetType}`);
  }

  steps = [
    {
      name: "candidateIsEligible",
      label: this.$tc("steps.candidateIsEligible"),
      isActive: true,
    },
    {
      name: STEP_NAME_GET_SPONSOR_LICENCE,
      label: this.$tc("steps.getSponsorLicence"),
      isActive: false,
    },
    {
      name: "assignCoS",
      label: this.$tc("steps.assignCoS"),
      isActive: false,
    },
    {
      name: "submitVisa",
      label: this.$tc("steps.submitVisa"),
      isActive: false,
    },
    {
      name: "visaDecision",
      label: this.$tc("steps.visaDecision"),
      isActive: false,
    },
  ];

  get computedSteps() {
    const steps = [...this.steps];

    if (this.isSponsor) {
      const stepIndex = steps.findIndex((step) => step.name === STEP_NAME_GET_SPONSOR_LICENCE);

      if (stepIndex > -1) steps.splice(stepIndex, 1);
    }

    return steps;
  }

  get statusLabel(): string {
    return this.$tc(`statuses.${this.status}.status`);
  }

  get employerStatusLabel(): string {
    const suffix = this.showStartVisaApplicationButton ? "slFinished" : "notFinished";

    return this.$tc(`statuses.${this.status}.employerInside.${suffix}`);
  }

  get widgetActionText() {
    const actionText = this.isSponsor
      ? this.$tc("actionText.isSponsor")
      : this.$tc("actionText.isNotSponsor");

    const actionItemsTitle = this.isSponsor
      ? this.$tc("actionItemsTitle.isSponsor")
      : this.$tc("actionItemsTitle.isNotSponsor");

    return {
      actionText,
      actionItemsTitle,
      status: {
        employerInside: this.employerStatusLabel,
      },
    };
  }

  get actionItems(): string {
    return this.$tc("actionItems");
  }

  get name(): string {
    if (this.status === HIGH_RISK_STATUS) return "";

    return this.isCandidate
      ? `${this.$tc("name.youAre")} `
      : `${this.report.user_name} ${this.$tc("name.is")} `;
  }

  get comment(): string {
    if (this.status === HIGH_RISK_STATUS) return "";

    if (this.status === LOW_RISK_STATUS) return `${this.name} ${this.$tc("comments.lowRisk")}`;

    if (this.widgetType === CANDIDATE_WIDGET_TYPE)
      return `${this.name} ${this.$tc("comments.candidateLowRisk")}`;

    return `${this.name} ${this.$tc("comments.mediumRisk")}`;
  }

  get caseId(): number {
    return Number(this.$route.params.id ?? 0);
  }

  // For "Outside UK" and "Inside UK" users;
  showSkilledWorkerPopup = false;

  openSkilledWorkerPopup() {
    // TO DO: refactor
    this.$root.$emit("open-start-sw-popup");
    // this.showSkilledWorkerPopup = true;
  }

  closeSkilledWorkerPopup() {
    this.showSkilledWorkerPopup = false;

    this.openSubmitRequestVisaPopup();
  }

  get cosType(): string {
    if (this.isInside) return COS_SKILLED_WORKER_INSIDE_SERVICE;

    if (this.isOutside) return COS_SKILLED_WORKER_SERVICE;

    return "";
  }

  // For "Inside UK" users;
  submitRequestVisaPopup = false;

  openSubmitRequestVisaPopup(): void {
    this.submitRequestVisaPopup = true;
  }

  closeSubmitRequestVisaPopup(): void {
    this.submitRequestVisaPopup = false;
  }

  showRequestSponsorLicenceDialog = false;

  submitRequestSponsorLicenceVisa(): void {
    this.showRequestSponsorLicenceDialog = true;
  }

  updateReport(): void {
    this.$emit("update-report");
  }

  get isArchived() {
    return SMCases.isArchived;
  }

  get isCandidateAssessment() {
    return SMCases.caseType === CANDIDATE_ASSESSMENT_SERVICE_TYPE;
  }

  get canUnarchive(): boolean {
    if (SMUser.isCandidate || SMUser.isEmployee) return false;

    return SMCases.isArchived && this.isCandidateAssessment && !this.isCosPlusSwStarted;
  }

  get isECComplete() {
    return EC_COMPLETED_STATUSES.includes(SMCases.caseStatus);
  }

  get canArchive(): boolean {
    if (SMUser.isCandidate || SMUser.isEmployee) return false;

    if (!this.isECComplete) return false;

    if (SMCases.isArchived) return false;

    return this.isCandidateAssessment && !this.isCosPlusSwStarted;
  }
}
